import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import { EASE, viewportOnce } from "@/lib/motion";
import { EXPERTISES, IMAGES, PILLARS } from "@/lib/data";
import { Fade, Mask } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import Marquee from "@/components/Marquee";

const h1Class =
  "font-extrabold uppercase leading-[0.82] tracking-tightest text-[clamp(4.2rem,17vw,15rem)]";

const HeroLine = ({
  children,
  delay,
  className = "",
}: {
  children: React.ReactNode;
  delay: number;
  className?: string;
}) => {
  const rm = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block will-change-transform ${className}`}
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={rm ? { duration: 0 } : { duration: 1.2, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const Hero = () => {
  const rm = useReducedMotion();
  const ref = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div
        className="absolute inset-0"
        style={rm ? undefined : { y, scale }}
        data-testid="hero-background"
      >
        <img
          src={IMAGES.hero}
          alt="Systèmes d'éclairage de scène lors d'une production événementielle — SONOCO EVENTS"
          className="h-full w-full object-cover opacity-45 grayscale"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-bg/70 via-bg/40 to-bg" />
      <div className="absolute inset-0 bg-gradient-to-r from-bg/60 to-transparent" />

      <div className="relative mx-auto w-full max-w-[1600px] px-5 sm:px-10 lg:px-16">
        <div className="flex items-start justify-between pt-28 sm:pt-36">
          <HeroLine delay={0.05}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute sm:text-[11px]">
              Production technique événementielle
            </p>
          </HeroLine>
          <HeroLine delay={0.15} className="hidden sm:block">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute sm:text-[11px]">
              Fréjus — Var · France
            </p>
          </HeroLine>
        </div>

        <h1 className="mt-8 sm:mt-14" data-testid="hero-title">
          <HeroLine delay={0.2} className={h1Class}>
            Sonoco<sup className="ml-2 align-super text-[0.2em] font-medium text-mute">®</sup>
          </HeroLine>
          <HeroLine delay={0.32} className={h1Class}>
            Events
          </HeroLine>
        </h1>

        <div className="flex flex-col gap-8 pb-10 pt-10 sm:flex-row sm:items-end sm:justify-between sm:pb-14">
          <HeroLine delay={0.5}>
            <p className="max-w-md text-lg font-light text-ink sm:text-xl">
              La technique au service de vos idées.
            </p>
          </HeroLine>
          <Fade delay={0.7} y={16}>
            <Link
              to="/expertises"
              data-testid="hero-cta-expertises"
              className="group inline-flex items-center gap-3 border border-ink/30 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-bg"
            >
              Découvrir nos expertises
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Fade>
        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-mute">Défiler</span>
        <span className="scroll-line block h-12 w-px bg-ink/50" />
      </div>
    </section>
  );
};

const Intro = () => (
  <section className="border-t border-line">
    <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-36 lg:px-16">
      <SectionLabel num="01" name="Le studio" />
      <h2 className="mt-10 text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-[0.9] tracking-tightest">
        <Mask>Donner forme</Mask>
        <Mask delay={0.1}>à vos ambitions.</Mask>
      </h2>

      <div className="mt-16 grid gap-14 lg:grid-cols-12">
        <Fade className="max-w-2xl space-y-8 lg:col-span-7" delay={0.1}>
          <p className="text-xl font-light leading-relaxed text-ink sm:text-2xl">
            SONOCO EVENTS accompagne les agences, entreprises et professionnels de l'événementiel
            dans la conception et la réalisation technique de leurs projets.
          </p>
          <p className="text-lg leading-relaxed text-mute">
            Sonorisation, éclairage, infrastructures scéniques et scénographie : nous réunissons
            expertise, créativité et maîtrise technique pour donner vie à vos événements.
          </p>
        </Fade>
        <Fade className="lg:col-span-4 lg:col-start-9" delay={0.2}>
          <div className="overflow-hidden border border-line">
            <motion.img
              src={IMAGES.intro}
              alt="Public lors d'un événement nocturne produit par SONOCO EVENTS"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
              initial={{ scale: 1.1 }}
              whileInView={{ scale: 1 }}
              viewport={viewportOnce}
              transition={{ duration: 1.4, ease: EASE }}
            />
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
            Production live — Sud de la France
          </p>
        </Fade>
      </div>

      <Fade className="mt-24 grid grid-cols-2 border-l border-t border-line lg:grid-cols-4" delay={0.1}>
        {PILLARS.map((p) => (
          <div key={p.num} className="border-b border-r border-line p-6 sm:p-8">
            <span className="font-mono text-[10px] tracking-[0.25em] text-faint">{p.num}</span>
            <h3 className="mt-4 text-lg font-bold uppercase tracking-tight sm:text-xl">
              {p.name}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-mute">{p.desc}</p>
          </div>
        ))}
      </Fade>
    </div>
  </section>
);

const ExpertiseList = () => {
  return (
    <section className="border-t border-line">
      <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-36 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionLabel num="02" name="Expertises" />
            <h2 className="mt-10 text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-[0.9] tracking-tightest">
              <Mask>Nos expertises.</Mask>
            </h2>
          </div>
          <Fade y={16}>
            <p className="max-w-xs pb-2 text-sm leading-relaxed text-mute">
              Cinq départements techniques, une seule exigence : une exécution maîtrisée.
            </p>
          </Fade>
        </div>

        <div className="relative mt-16">
          {EXPERTISES.map((ex, i) => (
            <Fade key={ex.slug} delay={i * 0.05} y={20}>
              <Link
                to={`/expertises#${ex.slug}`}
                data-testid={`home-expertise-row-${ex.slug}`}
                className="group flex items-center gap-4 border-t border-line py-6 sm:gap-10 sm:py-8"
              >
                <span className="w-10 shrink-0 font-mono text-xs text-mute">{ex.num} /</span>
                <span className="min-w-0 flex-1 break-words text-xl font-bold uppercase leading-[0.95] tracking-tight text-ink/90 transition-all duration-300 group-hover:translate-x-2 group-hover:text-ink sm:text-4xl sm:leading-none lg:text-6xl">
                  {ex.name}
                </span>
                <ArrowUpRight className="hidden h-8 w-8 shrink-0 text-mute transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-ink sm:block" />
              </Link>
            </Fade>
          ))}
          <span className="block border-t border-line" />
        </div>
      </div>
    </section>
  );
};

const emptyFrames = ["A", "B", "C"];

const RealisationsTeaser = () => (
  <section className="border-t border-line">
    <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-36 lg:px-16">
      <SectionLabel num="03" name="Réalisations" />
      <h2 className="mt-10 text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-[0.9] tracking-tightest">
        <Mask>Nos réalisations.</Mask>
      </h2>
      <Fade className="mt-8 max-w-2xl" delay={0.1}>
        <p className="text-lg leading-relaxed text-mute">
          Nous préparons actuellement la présentation de nos productions récentes. Les premières
          études de cas seront publiées ici prochainement.
        </p>
      </Fade>

      <Fade className="mt-14 grid gap-4 sm:grid-cols-3" delay={0.15}>
        {emptyFrames.map((f, i) => (
          <div
            key={f}
            data-testid={`home-project-placeholder-${i + 1}`}
            className="flex aspect-[4/3] items-center justify-center border border-dashed border-line"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
              Étude de cas — à venir
            </span>
          </div>
        ))}
      </Fade>

      <Fade className="mt-10" delay={0.2}>
        <Link
          to="/realisations"
          data-testid="home-realisations-link"
          className="link-sweep inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-ink"
        >
          Voir la page réalisations
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Fade>
    </div>
  </section>
);

const Philosophy = () => (
  <section className="border-t border-line bg-panel">
    <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-40 lg:px-16">
      <SectionLabel num="04" name="Philosophie" />
      <h2 className="mt-10 text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-[0.9] tracking-tightest">
        <Mask>Chaque détail compte.</Mask>
      </h2>
      <div className="mt-14 grid max-w-5xl gap-10 lg:grid-cols-2">
        <Fade delay={0.1}>
          <p className="text-lg leading-relaxed text-mute">
            Une réalisation réussie repose sur une préparation rigoureuse, des solutions techniques
            adaptées et une exécution maîtrisée.
          </p>
        </Fade>
        <Fade delay={0.2}>
          <p className="text-lg leading-relaxed text-mute">
            De l'étude initiale au démontage, SONOCO EVENTS accompagne chaque étape de la
            production technique.
          </p>
        </Fade>
      </div>
    </div>
  </section>
);

const ContactCta = () => (
  <section className="border-t border-line">
    <div className="mx-auto max-w-[1600px] px-5 py-28 sm:px-10 sm:py-44 lg:px-16">
      <SectionLabel num="05" name="Contact" />
      <Link to="/contact" data-testid="home-cta-contact" className="group mt-10 block">
        <Mask>
          <span className="block text-[clamp(3rem,10vw,9rem)] font-extrabold uppercase leading-[0.9] tracking-tightest">
            Un projet ?
          </span>
        </Mask>
        <Mask delay={0.12}>
          <span className="inline-flex items-center gap-4 text-[clamp(3rem,10vw,9rem)] font-extrabold uppercase leading-[0.9] tracking-tightest text-mute transition-colors duration-500 group-hover:text-ink">
            Parlons-en.
            <ArrowUpRight className="h-[0.62em] w-[0.62em] transition-transform duration-500 group-hover:-translate-y-3 group-hover:translate-x-3" />
          </span>
        </Mask>
      </Link>
    </div>
  </section>
);

export default function Home() {
  return (
    <main id="contenu">
      <Seo
        title="SONOCO EVENTS — Production Technique Événementielle | Fréjus, Var"
        description="Prestataire technique événementiel à Fréjus (Var) : sonorisation, éclairage, régie technique, structures scéniques et scénographie pour les professionnels de l'événementiel. Sud de la France et PACA."
        path="/"
      />
      <Hero />
      <Intro />
      <ExpertiseList />
      <RealisationsTeaser />
      <Philosophy />
      <Marquee />
      <ContactCta />
    </main>
  );
}
